import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getOrderStatusStyle } from "../../constants/statusStyles";
import { tokens } from "../../theme";
import DataTable from "../ui/DataTable";
import { Num, OrderStatusChip } from "../ui";
import { IconChevronRight, IconPen } from "../ui/icons";
import { formatAmountShort } from "../../utils/formatters";
import { getInitials } from "../../utils/initials";
import { useIsMobile } from "../../hooks/useIsMobile";

/** Iniciales del operador; en rojo cuando la orden no tiene a nadie asignado. */
const OperatorCell = ({ name }) => {
  const unassigned = !name || name === "Sin operador";
  const initials = unassigned ? "?" : getInitials(name);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: 999,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10.5px",
          fontWeight: 600,
          textTransform: "uppercase",
          backgroundColor: unassigned ? tokens.brandTint : tokens.borderSoft,
          color: unassigned ? tokens.brandDark : tokens.ink2,
        }}
      >
        {initials}
      </Box>
      <Typography
        sx={{
          fontSize: "13px",
          color: unassigned ? tokens.brandDark : tokens.ink2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {unassigned ? "Sin operador" : name}
      </Typography>
    </Box>
  );
};

const OrdersTable = ({
  orders,
  isAdmin,
  loading = false,
  totalCount = 0,
  rowsPerPage = 15,
  sortOrder = { name: "orderId", direction: "desc" },
  onTableChange,
  onMobileSortToggle,
  search,
  dateRange,
  chips,
  filters,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);

  const canEditOrder = (item) => isAdmin || item.status === 1;

  const openOrder = (item) => {
    if (!canEditOrder(item)) return;
    if (isAdmin) {
      navigate("/order-details-form", { state: { order: item, source: "ordersTable" } });
    } else {
      navigate("/edit-order", { state: { order: item } });
    }
  };

  const renderActions = (_value, tableMeta) => {
    const item = orders[tableMeta?.rowIndex];
    if (!item) return null;
    const enabled = canEditOrder(item);

    return (
      <Tooltip title={enabled ? "Editar orden" : "Esta orden ya no se puede editar"} arrow>
        <Box
          // Botón real, como el resto de las acciones de tabla: con un <div>
          // no se llegaba con Tab ni se activaba con Enter.
          component="button"
          type="button"
          aria-label={enabled ? "Editar orden" : "Esta orden ya no se puede editar"}
          disabled={!enabled}
          onClick={() => openOrder(item)}
          sx={{
            width: 32,
            height: 32,
            padding: 0,
            border: "none",
            background: "transparent",
            font: "inherit",
            borderRadius: `${tokens.radiusControl}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "auto",
            cursor: enabled ? "pointer" : "not-allowed",
            color: enabled ? tokens.ink2 : tokens.ink4,
            opacity: enabled ? 1 : 0.55,
            "&:hover": enabled ? { backgroundColor: tokens.borderSoft, color: tokens.brandDark } : undefined,
          }}
        >
          <IconPen size={16} />
        </Box>
      </Tooltip>
    );
  };

  const mobileColumns = [
    {
      name: "sortField",
      label: (
        <Box
          onClick={onMobileSortToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            minHeight: 44,
          }}
        >
          Orden
          <Box component="span" sx={{ fontSize: "0.8rem", marginLeft: "5px" }}>
            {sortOrder?.direction === "asc" ? "↑" : "↓"}
          </Box>
        </Box>
      ),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const item = orders[tableMeta.rowIndex];
          if (!item) return null;
          return <MobileOrderRow item={item} isAdmin={isAdmin} onOpen={() => openOrder(item)} />;
        },
      },
    },
  ];

  const desktopColumns = [
    {
      name: "orderId",
      label: "N° de orden",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Num sx={{ fontSize: "13px", fontWeight: 600 }}>#{value}</Num>
        ),
      },
    },
    {
      name: "client.company",
      label: "Cliente",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <Typography sx={{ fontSize: "13.5px", fontWeight: 500 }}>
            {value || "Sin cliente"}
          </Typography>
        ),
      },
    },
    {
      name: "employeeName",
      label: "Operador",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => <OperatorCell name={value} />,
      },
    },
    ...(isAdmin
      ? [
          {
            name: "totalAmount",
            label: "Monto",
            options: {
              filter: false,
              sort: true,
              setCellProps: () => ({ style: { textAlign: "right" } }),
              setCellHeaderProps: () => ({ style: { textAlign: "right" } }),
              customBodyRender: (value) => (
                <Num sx={{ fontSize: "13px", fontWeight: 500 }}>{formatAmountShort(value)}</Num>
              ),
            },
          },
        ]
      : []),
    {
      name: "status",
      label: "Estado",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => <OrderStatusChip status={value} />,
      },
    },
    {
      name: "date",
      label: "Fecha",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const item = orders[tableMeta.rowIndex];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
              <Num sx={{ fontSize: "13px" }}>{value}</Num>
              <Num sx={{ fontSize: "11.5px", color: tokens.ink3 }}>
                {(item?.time || "").slice(0, 5)}
              </Num>
            </Box>
          );
        },
      },
    },
    {
      name: "acciones",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ style: { textAlign: "right", width: 64 } }),
        setCellHeaderProps: () => ({ style: { textAlign: "right" } }),
        customBodyRender: renderActions,
      },
    },
  ];

  const columns = isMobile ? mobileColumns : desktopColumns;

  return (
    <DataTable
      data={orders}
      columns={columns}
      loading={loading}
      search={search}
      dateRange={dateRange}
      chips={chips}
      filters={filters}
      resultCount={orders.length}
      totalCount={totalCount}
      emptyMessage="No hay órdenes que coincidan con el filtro"
      options={{
        serverSide: true,
        count: totalCount,
        rowsPerPage,
        rowsPerPageOptions: isMobile ? [10, 20, 50] : [15, 30, 50, 100],
        // Sin esto la columna "client.company" queda vacía y muestra "Sin cliente".
        enableNestedDataAccess: ".",
        sort: !isMobile,
        sortOrder,
        // Los filtros de la barra ya van al servidor; el panel avanzado
        // de la librería filtra sobre la página cargada.
        filter: !isMobile,
        viewColumns: !isMobile,
        downloadOptions: {
          filename: "ordenes.csv",
          separator: ";",
          filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
        },
        onTableChange: (action, tableState) => {
          if (onTableChange) onTableChange(action, tableState);
        },
      }}
    />
  );
};

/** En mobile la tabla se vuelve tarjeta: nada de columnas apretadas. */
function MobileOrderRow({ item, isAdmin, onOpen }) {
  const style = getOrderStatusStyle(item.status);

  return (
    <Box
      onClick={onOpen}
      sx={{
        display: "flex",
        alignItems: "stretch",
        gap: "11px",
        padding: "4px 0",
        minHeight: 68,
        cursor: "pointer",
      }}
    >
      <Box sx={{ width: "3px", borderRadius: "2px", flexShrink: 0, backgroundColor: style.dot }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <Num sx={{ fontSize: "13.5px", fontWeight: 600 }}>#{item.orderId}</Num>
          <OrderStatusChip status={item.status} size="sm" />
          <Box sx={{ flexGrow: 1 }} />
          <Num sx={{ fontSize: "11.5px", color: tokens.ink3 }}>
            {item.date} · {(item.time || "").slice(0, 5)}
          </Num>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "14.5px",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.client?.company || "Sin cliente"}
            </Typography>
            <OperatorCell name={item.employeeName} />
          </Box>
          {isAdmin && (
            <Num sx={{ fontSize: "15px", fontWeight: 600, flexShrink: 0 }}>
              {formatAmountShort(item.totalAmount)}
            </Num>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", color: tokens.ink4, flexShrink: 0 }}>
        <IconChevronRight size={18} />
      </Box>
    </Box>
  );
}

export default OrdersTable;
